/**
 * Web Push subscription lifecycle.
 *
 * - loadInitialPushState() : check permission + subscription au boot
 * - requestAndSubscribe()  : ask permission → subscribe → POST /me/devices
 * - unsubscribe()          : DELETE /me/devices + pushManager.unsubscribe
 *
 * Le service worker (sw.js / service-worker.ts) doit être déjà register'é
 * (fait dans main.ts via virtual:pwa-register).
 */

import {
  meDevicesPost,
  meDevicesDelete,
  getVapidPublicKey,
} from "../workerClient";
import { getAccessToken } from "../auth/oauth";
import { pushState } from "./store.svelte";

/**
 * Décode base64url → Uint8Array sur ArrayBuffer (pas SharedArrayBuffer).
 * pushManager.subscribe veut explicitement Uint8Array<ArrayBuffer> en TS 5.7+,
 * donc on construit le buffer explicitement plutôt que `new Uint8Array(len)`
 * (qui renvoie Uint8Array<ArrayBufferLike> ambigu).
 */
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const pad = "=".repeat((4 - (base64.length % 4)) % 4);
  const std = (base64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(std);
  const buffer = new ArrayBuffer(bin.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg ?? null;
}

/** À appeler une fois au boot. Hydrate `pushState` depuis l'état navigateur. */
export async function loadInitialPushState(): Promise<void> {
  // Permission OS (synchrone)
  if ("Notification" in window) {
    pushState.permission = Notification.permission;
  } else {
    pushState.permission = "denied"; // Notifications API absente = mort
    return;
  }

  // Subscription existante côté browser
  const reg = await getRegistration();
  if (!reg) {
    pushState.subscribed = false;
    return;
  }
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    pushState.subscribed = true;
    pushState.endpointHost = new URL(sub.endpoint).host;
  } else {
    pushState.subscribed = false;
    pushState.endpointHost = null;
  }
}

/**
 * Flow complet : ask permission → subscribe → POST /me/devices.
 *
 * Throw si permission refusée ou échec API. Caller (UI) catch et affiche.
 */
export async function requestAndSubscribe(): Promise<void> {
  if (pushState.busy) return;
  pushState.busy = true;
  try {
    // 1. Permission OS — peut être accordée silencieusement si déjà 'granted'
    if (pushState.permission !== "granted") {
      const result = await Notification.requestPermission();
      pushState.permission = result;
      if (result !== "granted") {
        throw new Error(
          result === "denied"
            ? "Permission refusée. Active les notifications dans Réglages iOS pour Safari."
            : "Permission abandonnée — re-essaie.",
        );
      }
    }

    const reg = await getRegistration();
    if (!reg) throw new Error("Service worker pas prêt — refresh la page");

    // 2. Récupère la VAPID public key du Worker (pas hardcodée pour pouvoir
    //    rotater sans redéployer la PWA)
    const vapidPublicKey = await getVapidPublicKey();

    // 3. Subscribe via pushManager
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });
    }

    // 4. POST /me/devices au Worker (auth Google token)
    const accessToken = await getAccessToken();
    if (!accessToken) {
      throw new Error("Pas signé in avec Google — re-sign-in d'abord");
    }
    await meDevicesPost(accessToken, {
      subscription: sub.toJSON(),
      user_agent: navigator.userAgent,
    });

    pushState.subscribed = true;
    pushState.endpointHost = new URL(sub.endpoint).host;
  } finally {
    pushState.busy = false;
  }
}

/**
 * Désabonne : DELETE côté Worker + pushManager.unsubscribe.
 * Appelé manuellement ou au sign-out (cf. oauth.ts).
 */
export async function unsubscribe(): Promise<void> {
  if (pushState.busy) return;
  pushState.busy = true;
  try {
    const reg = await getRegistration();
    if (!reg) {
      pushState.subscribed = false;
      return;
    }
    const sub = await reg.pushManager.getSubscription();
    if (!sub) {
      pushState.subscribed = false;
      return;
    }

    const endpoint = sub.endpoint;

    // 1. Best-effort DELETE côté Worker (avant que la subscription disparaisse)
    try {
      const accessToken = await getAccessToken();
      if (accessToken) {
        await meDevicesDelete(accessToken, endpoint);
      }
    } catch (e) {
      console.warn("DELETE /me/devices failed (continuing):", e);
    }

    // 2. Unsubscribe côté browser
    await sub.unsubscribe();

    pushState.subscribed = false;
    pushState.endpointHost = null;
  } finally {
    pushState.busy = false;
  }
}
